/**
 * ServiceOrdersListView - Listagem de Ordens de Serviço (página e modal de auditoria)
 *
 * Padrão oficial de listagens (ListPage* + KpiCard + SortableHeader). KPIs clicáveis
 * filtram pela situação; ordenação validada no servidor por allowlist.
 *
 * RELEASE: 2026-10-19 v1.13.2 - Filtro "Todos os prestadores" passa a usar o estilo pill padrão
 *            (quickFilter): sem borda cinza e cantos arredondados, igual aos demais filtros da barra.
 * RELEASE: 2026-10-19 v1.13.1 - Situação sem seleção exibe Todas as situações;
 *            opção all limpa o filtro em vez de enviar uma situação inexistente à RPC.
 * RELEASE: 2026-10-18 v1.13.0 - Cards de KPI ocultos na variante modal: o modal de auditoria dos
 *            KPIs do Dashboard de O.S. já traz a quantidade no título, então a linha de KPIs
 *            (Total/Abertas/Agendadas/...) é renderizada apenas na página oficial.
 * RELEASE: 2026-10-18 v1.12.0 - Corpo extraído de ServiceOrdersListPage para ServiceOrdersListView,
 *            reutilizado pelo modal de auditoria dos KPIs do Dashboard de O.S. (variant="modal"
 *            + fixedIds). Na variante modal ficam ocultos Calendário, Kanban e Nova OS.
 * RELEASE: 2026-10-10 v1.11.0 - Atalho Calendário abre o modal de calendário de OS
 *            (BR-OS-031) em vez de navegar para a página de Calendário Global.
 * RELEASE: 2026-10-09 v1.10.0 - Tabela extraída para ServiceOrdersTable (sem duplicação nas abas O.S).
 * RELEASE: 2026-10-09 v1.9.6 - Tamanhos de fonte padronizados com a Retenção: cabeçalhos
 *            simples com text-xs font-medium e células de data com text-xs.
 * RELEASE: 2026-10-09 v1.9.3 - Cronômetro e Situação movidos para antes da coluna Prioridade.
 * RELEASE: 2026-10-09 v1.9.2 - Header padronizado com a Retenção: busca centralizada
 *            na linha do título (slot `center` do ListPageHeader) e atalhos em botões
 *            de ícone compactos (Calendário/Kanban/Atualizar/Nova OS com variante mobile).
 * RELEASE: 2026-10-09 v1.6.1 - Rolagem da tabela alinhada à estrutura oficial de Retenção.
 * RELEASE: 2026-10-09 v1.6.0 - Densidade em duas linhas, Prioridade textual e tabela full-bleed.
 * RELEASE: 2026-10-09 v1.5.0 - Paridade com Retenção: ordem, badges, categoria, progresso e atalhos.
 * RELEASE: 2026-10-09 v1.4.0 - Barra de prioridade, documentos, placa textual, cronômetro operacional, prazo e agenda.
 * RELEASE: 2026-10-09 v1.3.0 - Paridade com Tarefas: prioridade, cronômetro, prazo, conclusão e filtros avançados.
 * RELEASE: 2026-10-08 v1.2.0 - Exportação da listagem com os filtros da tela.
 * RELEASE: 2026-10-08 v1.1.0 - Fase 5: KPIs, filtros, tabela ordenável e paginação.
 * RELEASE: 2026-10-08 v1.0.0 - Versão inicial (botão Nova OS).
 */
import { useEffect, useMemo, useState } from 'react';
import { ExportMenuButton } from '@/components/export/ExportMenuButton';
import { AlertTriangle, CalendarClock, CalendarDays, CheckCircle2, ClipboardList, Clock, Hourglass, Kanban, Plus, RefreshCw, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  KpiCard, ListPageContent, ListPageFiltersBar, ListPageHeader, ListPageKpis, ListPageLayout,
  LIST_FILTERS_INPUT_CLASS, LIST_FILTERS_SELECT_TRIGGER_CLASS,
} from '@/components/layout/list-page';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PaginationFooter } from '@/components/ui/pagination-footer';
import { PersonSelector } from '@/components/persons/PersonSelector';
import { usePermissionsContext } from '@/contexts/PermissionsContext';
import { useServiceTypes } from '@/hooks/useServiceTypes';
import {
  SERVICE_ORDER_STATUS_LABEL, useServiceOrdersList, type ServiceOrderSortField, type ServiceOrdersFilters,
} from '@/hooks/useServiceOrders';
import { TaskCreatedSuccessDialog } from '@/components/tasks/modal/TaskCreatedSuccessDialog';
import { ServiceOrderFormDialog } from '@/components/service-orders/ServiceOrderFormDialog';
// Fase 6: clique na OS abre o modal de detalhes
import { ServiceOrdersTable } from '@/components/service-orders/ServiceOrdersTable';
import { ServiceOrderDetailsModal } from '@/components/service-orders/ServiceOrderDetailsModal';
// v1.11.0: botão Calendário abre o modal de calendário de OS (BR-OS-031) em vez de navegar
import { ServiceOrderCalendarModal } from '@/components/service-orders/ServiceOrderCalendarModal';
import { cn } from '@/lib/utils';
import { useTaskCategories } from '@/hooks/useTaskCategories';
import { useTaskPhases } from '@/hooks/useTaskPhases';
import { SERVICE_ORDER_DEADLINE_OPTIONS } from '@/lib/serviceOrderTiming';
import { QUICK_FILTER_PILL } from '@/components/layout/list-page/quickFilterStyles';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';

const INITIAL: ServiceOrdersFilters = {
  search: '', status: null, serviceTypeId: null, providerId: null, categoryId: null, phaseId: null,
  deadlineBucket: null, dateField: 'created_at', dateFrom: null, dateTo: null,
  sortField: 'created_at', sortDirection: 'desc', page: 1, pageSize: 25,
};

export interface ServiceOrdersListViewProps {
  /** 'page' = página oficial; 'modal' = auditoria de KPI (sem atalhos de navegação/criação) */
  variant?: 'page' | 'modal';
  /** Lista fixa de O.S. (validada no servidor) — usada pela auditoria dos KPIs */
  fixedIds?: string[] | null;
  /** Título exibido no cabeçalho (padrão "Ordens de Serviço") */
  title?: string;
}

export function ServiceOrdersListView({ variant = 'page', fixedIds = null, title = 'Ordens de Serviço' }: ServiceOrdersListViewProps) {
  const isModal = variant === 'modal';
  const navigate = useNavigate();
  const { canCreate: canCreateMenu, canView, isAdmin } = usePermissionsContext() as any;
  const canCreate = isAdmin || !!canCreateMenu?.('os_list');
  const canOpenKanban = isAdmin || !!canView?.('os_kanban');
  const [filters, setFilters] = useState<ServiceOrdersFilters>({ ...INITIAL, ids: fixedIds });
  // Mantém a lista fixa sincronizada quando o KPI auditado muda
  useEffect(() => { setFilters((f) => ({ ...f, ids: fixedIds, page: 1 })); }, [fixedIds]);
  const [searchInput, setSearchInput] = useState('');
  const [dialog, setDialog] = useState<{ open: boolean; id: string | null }>({ open: false, id: null });
  // OS recém-criada: abre o modal de sucesso (mesmo de Tarefas)
  const [created, setCreated] = useState<{ id: string; number: string } | null>(null);
  const [detailsId, setDetailsId] = useState<string | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  // v1.11.0: controla o modal de calendário de OS aberto pelo atalho do cabeçalho
  const [calendarOpen, setCalendarOpen] = useState(false);
  const { serviceTypes } = useServiceTypes();
  const { categories } = useTaskCategories(true);
  const { phases } = useTaskPhases(filters.categoryId ?? undefined);
  const { data, isLoading, isFetching, refetch } = useServiceOrdersList(filters);
  const k = data?.kpis;
  // Exportação usa os mesmos filtros/ordenação da tela (sem paginação)
  const exportParams = useMemo(() => { const { page: _p, pageSize: _s, ...rest } = filters; return rest; }, [filters]);

  // Busca com espera de 350 ms
  useEffect(() => {
    const t = setTimeout(() => setFilters((f) => (f.search === searchInput.trim() ? f : { ...f, search: searchInput.trim(), page: 1 })), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const patch = (p: Partial<ServiceOrdersFilters>) => setFilters((f) => ({ ...f, ...p, page: p.page ?? 1 }));
  const sort = (field: ServiceOrderSortField) =>
    setFilters((f) => ({ ...f, sortField: field, sortDirection: f.sortField === field && f.sortDirection === 'desc' ? 'asc' : 'desc', page: 1 }));
  const toggleStatus = (s: ServiceOrdersFilters['status']) => patch({ status: filters.status === s ? null : s });

  return (
    <ListPageLayout>
      <ListPageHeader
        title={title}
        /* v1.9.2: busca premium centralizada na linha do título (padrão Retenção/TaskList).
           Oculta em mobile — nesse caso a busca fica na barra de filtros.
           v1.9.3: `w-full` garante a largura total do slot central (mesmo tamanho da listagem de Negócios);
           subtítulo removido do cabeçalho. */
        center={
          <div className="relative w-full max-w-md mx-4 hidden md:block">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/60" />
            <Input
              placeholder="Buscar número, cliente, placa ou prestador..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              maxLength={100}
              className="h-10 pl-10 pr-4 text-sm rounded-xl border-none bg-muted/40 hover:bg-muted/60 transition-all duration-200 focus-visible:bg-background focus-visible:shadow-md focus-visible:ring-1 focus-visible:ring-primary/20 focus-visible:ring-offset-0 placeholder:text-muted-foreground/50"
            />
          </div>
        }
        actions={
          <div className="flex items-center gap-1.5">
            {/* v1.9.2: atalhos padronizados como botões de ícone compactos (padrão Retenção) */}
            {/* v1.11.0: abre o modal de calendário de OS (com filtros de prestador/tipo) em vez da página de calendário */}
            {!isModal && (
            <Button variant="outline" size="icon-sm" onClick={() => setCalendarOpen(true)}
              title="Abrir calendário de agendamentos" aria-label="Abrir calendário de agendamentos">
              <CalendarDays />
            </Button>
            )}
            {!isModal && canOpenKanban && (
              <Button variant="outline" size="icon-sm" onClick={() => navigate('/tenant/service-orders/kanban')}
                title="Alternar para Kanban OS" aria-label="Alternar para Kanban OS">
                <Kanban />
              </Button>
            )}
            <Button variant="outline" size="icon-sm" onClick={() => refetch()} disabled={isFetching}
              title="Atualizar ordens de serviço" aria-label="Atualizar ordens de serviço">
              <RefreshCw className={cn(isFetching && 'animate-spin')} />
            </Button>
            <ExportMenuButton sourceId="service_orders" params={exportParams} variant="icon" />
            {/* Nova OS: primária com efeito hover no desktop; ícone compacto no mobile (padrão Retenção) */}
            {!isModal && canCreate && (
              <Button size="xs" className="hidden sm:inline-flex hover:shadow-md hover:scale-[1.02] transition-all duration-200"
                onClick={() => setDialog({ open: true, id: null })}>
                <Plus className="sm:mr-1.5" />
                <span className="hidden sm:inline">Nova OS</span>
              </Button>
            )}
            {!isModal && canCreate && (
              <Button size="icon-sm" className="sm:hidden" title="Nova OS" aria-label="Nova OS"
                onClick={() => setDialog({ open: true, id: null })}>
                <Plus />
              </Button>
            )}
          </div>
        }
      />
      {/* v1.13.0: os cards de KPI pertencem à página oficial; no modal de auditoria eles
          ficariam duplicando a contagem que já aparece no título do modal. */}
      {!isModal && (
        <ListPageKpis columns={7}>
          <KpiCard color="purple" icon={ClipboardList} label="Total" value={k?.total ?? 0} onClick={() => patch({ status: null })} />
          <KpiCard color="blue" icon={Clock} label="Abertas" value={k?.open ?? 0} onClick={() => toggleStatus('open')} active={filters.status === 'open'} />
          <KpiCard color="cyan" icon={CalendarClock} label="Agendadas" value={k?.scheduled ?? 0} onClick={() => toggleStatus('scheduled')} active={filters.status === 'scheduled'} />
          <KpiCard color="yellow" icon={Hourglass} label="Em andamento" value={k?.in_progress ?? 0} onClick={() => toggleStatus('in_progress')} active={filters.status === 'in_progress'} />
          <KpiCard color="orange" icon={ClipboardList} label="Aguardando revisão" value={k?.awaiting_review ?? 0} onClick={() => toggleStatus('awaiting_review')} active={filters.status === 'awaiting_review'} />
          <KpiCard color="green" icon={CheckCircle2} label="Concluídas" value={k?.completed ?? 0} onClick={() => toggleStatus('completed')} active={filters.status === 'completed'} />
          <KpiCard color="red" icon={AlertTriangle} label="Atrasadas" value={k?.overdue ?? 0} onClick={() => toggleStatus('overdue')} active={filters.status === 'overdue'} />
        </ListPageKpis>
      )}
      <ListPageFiltersBar isFetching={isFetching} onMoreClick={() => setFiltersOpen(true)} moreCount={Number(!!filters.categoryId) + Number(!!filters.phaseId)}>
        {/* v1.9.2: busca visível apenas em mobile (no desktop fica centralizada no header, padrão Retenção) */}
        <Input className={cn(LIST_FILTERS_INPUT_CLASS, 'min-w-0 flex-1 sm:max-w-xs md:hidden')} value={searchInput} maxLength={100}
          placeholder="Buscar número, cliente, placa ou prestador..." onChange={(e) => setSearchInput(e.target.value)} />
        {/* O seletor usa all; o estado e a RPC usam null para não restringir a situação. */}
        <Select value={filters.status ?? 'all'} onValueChange={(v) => patch({ status: v === 'all' ? null : v as ServiceOrdersFilters['status'] })}>
          <SelectTrigger aria-label="Situação" className={cn(LIST_FILTERS_SELECT_TRIGGER_CLASS, QUICK_FILTER_PILL, 'shrink-0')}><SelectValue placeholder="Todas as situações" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as situações</SelectItem>
            {Object.entries(SERVICE_ORDER_STATUS_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
            <SelectItem value="overdue">Atrasadas</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.serviceTypeId ?? 'all'} onValueChange={(v) => patch({ serviceTypeId: v === 'all' ? null : v })}>
          <SelectTrigger className={cn(LIST_FILTERS_SELECT_TRIGGER_CLASS, QUICK_FILTER_PILL, 'shrink-0')}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os tipos</SelectItem>
            {serviceTypes.map((t) => <SelectItem key={t.id} value={t.id}><span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: t.color }} />{t.name}</span></SelectItem>)}
          </SelectContent>
        </Select>
        {/* quickFilter: o botão assume o pill padrão (sem borda) e a lista ampla com nomes completos. */}
        <PersonSelector role="service_provider" quickFilter value={filters.providerId} onSelect={(id) => patch({ providerId: id })}
          placeholder="Todos os prestadores" noneLabel="Todos os prestadores"
          className={cn(LIST_FILTERS_SELECT_TRIGGER_CLASS, 'w-48 shrink-0')} />
        <Select value={filters.dateField} onValueChange={(value) => patch({ dateField: value as ServiceOrdersFilters['dateField'] })}>
          <SelectTrigger className={cn(LIST_FILTERS_SELECT_TRIGGER_CLASS, QUICK_FILTER_PILL, 'w-32 shrink-0')}><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="created_at">Criação</SelectItem><SelectItem value="scheduled_start">Agendamento</SelectItem><SelectItem value="completed_at">Conclusão</SelectItem></SelectContent>
        </Select>
        <Input type="date" aria-label="De" className={cn(LIST_FILTERS_INPUT_CLASS, QUICK_FILTER_PILL, 'w-36 shrink-0')} value={filters.dateFrom ?? ''}
          onChange={(e) => patch({ dateFrom: e.target.value || null })} />
        <Input type="date" aria-label="Até" className={cn(LIST_FILTERS_INPUT_CLASS, QUICK_FILTER_PILL, 'w-36 shrink-0')} value={filters.dateTo ?? ''}
          onChange={(e) => patch({ dateTo: e.target.value || null })} />
        <Select value={filters.deadlineBucket ?? 'all'} onValueChange={(value) => patch({ deadlineBucket: value === 'all' ? null : value as ServiceOrdersFilters['deadlineBucket'] })}>
          <SelectTrigger className={cn(LIST_FILTERS_SELECT_TRIGGER_CLASS, QUICK_FILTER_PILL, 'w-32 shrink-0')}><SelectValue placeholder="Prazo" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Todos os prazos</SelectItem>{SERVICE_ORDER_DEADLINE_OPTIONS.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent>
        </Select>
      </ListPageFiltersBar>
      <ListPageContent fullBleed>
          {/* v1.10.0: tabela extraída para ServiceOrdersTable (reutilizada nas abas O.S dos modais) */}
          <ServiceOrdersTable items={data?.items ?? []} isLoading={isLoading} sortField={filters.sortField}
            sortDirection={filters.sortDirection} onSort={sort} onOpen={setDetailsId} />
        <div className="border-t px-4 py-2">
          <PaginationFooter totalItems={data?.total ?? 0} currentPage={filters.page} pageSize={filters.pageSize}
            onPageChange={(page) => setFilters((f) => ({ ...f, page }))}
            onPageSizeChange={(pageSize) => setFilters((f) => ({ ...f, pageSize, page: 1 }))}
            pageSizeOptions={[10, 25, 50, 100]} borderClassName="" className="mt-0 pt-0" />
        </div>
      </ListPageContent>
      <ServiceOrderFormDialog open={dialog.open} onOpenChange={(open) => setDialog((d) => ({ ...d, open }))} serviceOrderId={dialog.id} onCreated={setCreated} />
      <TaskCreatedSuccessDialog open={!!created} onOpenChange={(o) => !o && setCreated(null)} taskNumber={0}
        numberText={created?.number} title="OS Criada com Sucesso!" numberLabel="Número da OS" viewLabel="Visualizar OS"
        description="A ordem de serviço foi criada e já está disponível no sistema. Você pode visualizá-la para adicionar mais detalhes ou andamentos."
        onViewTask={() => { if (created) setDetailsId(created.id); setCreated(null); }} />
      <ServiceOrderDetailsModal open={!!detailsId} onOpenChange={(o) => !o && setDetailsId(null)} serviceOrderId={detailsId} />
      {/* v1.11.0: modal de calendário de agendamentos de OS (atalho do cabeçalho) */}
      <ServiceOrderCalendarModal open={calendarOpen} onOpenChange={setCalendarOpen} />
      <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
        <SheetContent className="w-full sm:max-w-md">
          <SheetHeader><SheetTitle>Filtros avançados</SheetTitle><SheetDescription>Refine as ordens pela estrutura de categoria e fase.</SheetDescription></SheetHeader>
          <div className="mt-6 space-y-5">
            <div className="space-y-2"><label className="text-sm font-medium">Categoria</label><Select value={filters.categoryId ?? 'all'} onValueChange={(value) => patch({ categoryId: value === 'all' ? null : value, phaseId: null })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">Todas as categorias</SelectItem>{categories.map((category) => <SelectItem key={category.id} value={category.id}><span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: category.color }} />{category.name}</span></SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-2"><label className="text-sm font-medium">Fase</label><Select value={filters.phaseId ?? 'all'} disabled={!filters.categoryId} onValueChange={(value) => patch({ phaseId: value === 'all' ? null : value })}><SelectTrigger><SelectValue placeholder="Selecione uma categoria" /></SelectTrigger><SelectContent><SelectItem value="all">Todas as fases</SelectItem>{phases.map((phase: any) => <SelectItem key={phase.id} value={phase.id}><span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: phase.color }} />{phase.name}</span></SelectItem>)}</SelectContent></Select></div>
            <Button variant="outline" className="w-full" onClick={() => patch({ categoryId: null, phaseId: null })}>Limpar filtros avançados</Button>
          </div>
        </SheetContent>
      </Sheet>
    </ListPageLayout>
  );
}
